import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFeatureFlag } from '@ibk/shared-feature-flags';
import { useTranslation } from '@ibk/shared-translation';
import { FeatureFlagUseCase } from '@ibk/shared-types';
import { PixType } from '../../enums';
import { usePostValidateDuplicate } from '../usePostValidateDuplicate';
export function usePixTransferSteps() {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { isFeatureFlagWhiteListed } = useFeatureFlag();
    const [showAlert, setShowAlert] = useState(false);
    const [currentStepIndex, setCurrentStepIndex] = useState(0);
    const [viewReceipt, setViewReceipt] = useState(false);
    const [loading] = useState(false);
    const [error] = useState(false);
    const [selectedKeyType, setSelectedKeyType] = useState();
    const [selectedKeyValue, setSelectedKeyValue] = useState('');
    const [transferValue, setTransferValue] = useState(0);
    const [paymentDate, setPaymentDate] = useState();
    const [pixKeyDetails, setPixKeyDetails] = useState(null);
    const [showCalendar, setShowCalendar] = useState(false);
    const [addToContactList, setAddToContactList] = useState(false);
    const [isContactFromSavedList, setIsContactFromSavedList] = useState(false);
    const [alert, setAlert] = useState();
    const [showDuplicateModal, setShowDuplicateModal] = useState(false);
    const [showPixTimeoutError, setShowPixTimeoutError] = useState(false);
    const [showInsufficientBalanceModal, setShowInsufficientBalanceModal] = useState(false);
    const [confirmDuplicate, setConfirmDuplicate] = useState(false);
    const [transactionStartAt, setTransactionStartAt] = useState(null);
    const [pixAgencyAccount, setPixAgencyAccount] = useState(null);
    const [showPixLimitExceededModal, setShowPixLimitExceededModal] = useState(false);
    const [showPaymentAlreadyExistsModal, setShowPaymentAlreadyExistsModal] = useState(false);
    const isPixLimitsWhiteListed = isFeatureFlagWhiteListed(FeatureFlagUseCase.CORE_BANKING_PIX_LIMITS);
    const { postValidateDuplicate, isDuplicate, setIsDuplicate, loading: postValidateDuplicateLoading, } = usePostValidateDuplicate();
    const steps = useMemo(() => [
        {
            title: t('pix-transfer-steps-step-1-title'),
        },
        {
            title: t('pix-transfer-steps-step-2-title'),
        },
        {
            title: t('pix-transfer-steps-step-3-title'),
        },
    ], [t]);
    const isPixLimitExceededModalVisible = useMemo(() => {
        return isPixLimitsWhiteListed && showPixLimitExceededModal;
    }, [isPixLimitsWhiteListed, showPixLimitExceededModal]);
    const setStep = useCallback((step) => {
        setCurrentStepIndex(step);
    }, []);
    const handleGoHome = useCallback(() => {
        navigate('/conta-pj');
    }, [navigate]);
    const handleCloseReceipt = useCallback(() => {
        setViewReceipt(false);
    }, []);
    const handleCloseOldAlert = useCallback(() => {
        setShowAlert(false);
    }, []);
    const isStepValid = useCallback((step) => {
        return step >= 0 && step <= 2;
    }, []);
    const canGoToNextStep = useCallback((currentStep) => {
        return currentStep < 2;
    }, []);
    const canGoToPreviousStep = useCallback((currentStep) => {
        return currentStep > 0;
    }, []);
    const handleKeyTypeSelect = useCallback((keyType, keyValue, details, isFromSavedContact, pixAgencyAccount) => {
        if (keyType !== PixType.AGENCY) {
            setPixAgencyAccount(null);
            setSelectedKeyType(keyType);
            setSelectedKeyValue(keyValue);
            setPixKeyDetails(details || null);
            setIsContactFromSavedList(isFromSavedContact || false);
            if (isFromSavedContact) {
                setAddToContactList(true);
            }
            else {
                setAddToContactList(false);
            }
        }
        else {
            setPixAgencyAccount(pixAgencyAccount || null);
            setSelectedKeyType(keyType);
            setSelectedKeyValue(keyValue);
            setPixKeyDetails(details || null);
            setIsContactFromSavedList(isFromSavedContact || false);
            setAddToContactList(isFromSavedContact || false);
        }
    }, []);
    const handleValueSet = useCallback((value) => {
        setTransferValue(value);
    }, []);
    const handlePaymentDateSet = useCallback((date) => {
        setPaymentDate(date);
    }, []);
    const handleAddToContactListChange = useCallback((checked) => {
        setAddToContactList(checked);
    }, []);
    useEffect(() => {
        if (isDuplicate === true) {
            setShowDuplicateModal(true);
        }
    }, [isDuplicate]);
    const onError = useCallback((error) => {
        if (error === t('payment-in-processing')) {
            setShowPaymentAlreadyExistsModal(true);
        }
        else if (error === t('pix-limit-exceeded')) {
            setShowPixLimitExceededModal(true);
        }
        else if (error === t('pix-timeout-error')) {
            setShowPixTimeoutError(true);
        }
        else if (error === t('insufficient-balance-modal-title')) {
            setShowInsufficientBalanceModal(true);
        }
        else {
            setAlert({
                message: error,
                appearance: 'error',
            });
        }
    }, [t]);
    const handleConfirmDuplicate = useCallback(() => {
        setShowDuplicateModal(false);
        setConfirmDuplicate(true);
        setIsDuplicate(false);
    }, [setIsDuplicate]);
    const onConfirmDuplicateOnRevisionStep = useCallback(() => {
        setConfirmDuplicate(true);
        setShowDuplicateModal(false);
    }, []);
    const handleCloseDuplicateModal = useCallback(() => {
        setShowDuplicateModal(false);
    }, []);
    const handleClosePixTimeoutError = useCallback(() => {
        setShowPixTimeoutError(false);
    }, []);
    const handleCloseInsufficientBalanceModal = useCallback(() => {
        setShowInsufficientBalanceModal(false);
    }, []);
    const handleCloseAlert = useCallback(() => {
        setAlert(undefined);
    }, []);
    const handleResetDuplicateStates = useCallback(() => {
        setConfirmDuplicate(false);
        setIsDuplicate(undefined);
        setShowDuplicateModal(false);
    }, [setIsDuplicate]);
    return {
        loading: loading || postValidateDuplicateLoading,
        currentStepIndex,
        showAlert,
        viewReceipt,
        error,
        showCalendar,
        showPixLimitExceededModal,
        isPixLimitExceededModalVisible,
        steps,
        isStepValid,
        canGoToNextStep,
        canGoToPreviousStep,
        setStep,
        handleGoHome,
        handleCloseReceipt,
        handleCloseAlert,
        setShowAlert,
        setViewReceipt,
        setShowCalendar,
        handleCloseOldAlert,
        setShowPixLimitExceededModal,
        selectedKeyType,
        selectedKeyValue,
        transferValue,
        paymentDate,
        pixKeyDetails,
        addToContactList,
        isContactFromSavedList,
        pixAgencyAccount,
        handleKeyTypeSelect,
        handleValueSet,
        handlePaymentDateSet,
        handleAddToContactListChange,
        alert,
        showDuplicateModal,
        showPixTimeoutError,
        showInsufficientBalanceModal,
        handleConfirmDuplicate,
        onConfirmDuplicateOnRevisionStep,
        handleCloseDuplicateModal,
        handleClosePixTimeoutError,
        handleCloseInsufficientBalanceModal,
        showPaymentAlreadyExistsModal,
        setShowPaymentAlreadyExistsModal,
        onError,
        postValidateDuplicate,
        confirmDuplicate,
        transactionStartAt,
        setTransactionStartAt,
        isDuplicate,
        setAlert,
        setShowDuplicateModal,
        setShowInsufficientBalanceModal,
        setShowPixTimeoutError,
        handleResetDuplicateStates,
    };
}
